import * as React from 'react'

import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Collapse from '@mui/material/Collapse'
import Typography from '@mui/material/Typography'

import { Note } from '../Note'
import { useData, useHandlers } from './hooks'
import { Chevron } from '@sales-center/components'
import { NoteCode } from '@user/components/Notes/typings'
import { Theme } from '@mui/material/styles'
import { selectAllEntityFormSettings } from '@user/store/entityFormSettings'
import { addLeadNote } from '@user/components/Settings/EntityFormSettings/constants'
import { useGetFieldActiveStates } from '@user/store/entityFormSettings/hooks/useGetFieldActiveStates'
import { useSelector } from 'react-redux'
import { useFormikContext } from 'formik'
import { IAddOpportunityFormModel } from '../../../typings'

type Props = {
  hideOptional: boolean
  noteCodes: NoteCode[]
}

export function SubHeader(props: Props) {
  const { hideOptional, noteCodes } = props
  const { drawerOpen, setDrawerOpen } = useData()

  const { handleClick } = useHandlers({ drawerOpen, setDrawerOpen })

  const settings = useSelector(selectAllEntityFormSettings)
  const entitySettingsMap = useGetFieldActiveStates(settings, hideOptional)

  const [_, showAddNote] = entitySettingsMap.get(addLeadNote) ?? [false, true]

  const { errors, touched, submitCount } =
    useFormikContext<IAddOpportunityFormModel>()

  const hasNoteSectionError =
    (!!errors.note && !!touched.note) ||
    (!!errors.noteCode && !!touched.noteCode) ||
    (!!errors.serviceLocation && !!touched.serviceLocation)

  React.useEffect(() => {
    if (hasNoteSectionError) {
      setDrawerOpen(true)
    }
  }, [submitCount, hasNoteSectionError, setDrawerOpen])

  if (!showAddNote) return null

  return (
    <Paper
      square
      sx={{
        backgroundColor: (theme: Theme) => theme.palette.common.white,
        paddingTop: (theme: Theme) => theme.spacing(drawerOpen ? 2 : 1.5),
        paddingBottom: (theme: Theme) => theme.spacing(drawerOpen ? 2 : 1.5),
        paddingX: 1,
        cursor: 'pointer',
      }}
    >
      <Stack
        onClick={handleClick}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        px={drawerOpen ? 2 : 1}
        pb={drawerOpen ? 2 : 0}
        data-testid="sub-header-note-toggle"
      >
        <Typography variant="body1">{'Note'}</Typography>
        <Chevron open={drawerOpen} dataTestId="opportunityNoteExpandChevron" />
      </Stack>
      <Collapse in={drawerOpen} timeout="auto" unmountOnExit>
        <Box display="flex" alignItems="center" py={3} pt={0} px={2}>
          <Note
            hideOptional={hideOptional}
            variant="standard"
            noteCodes={noteCodes}
            hideTitle={true}
          />
        </Box>
      </Collapse>
    </Paper>
  )
}
